import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaChartLine, FaFileExcel, FaFilePdf } from 'react-icons/fa';
import { jsPDF } from 'jspdf';
import { fetchPaymentsExcel, getAllSellers } from '../../../Redux/Actions/actions';
import RentalFilters from '../../../Components/RentalFilters/RentalFilters';
import style from './ContractSummaryReport.module.css';

// Estados que aplican al reporte de cuotas (no al contrato)
const PAYMENT_STATUS_OPTIONS = [
  { value: 'paid', label: 'Cobradas' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'partial', label: 'Parciales' },
  { value: 'overdue', label: 'Vencidas' },
  { value: 'cancelled', label: 'Canceladas' },
  { value: 'all', label: 'Todas' },
];

const STATUS_LABELS = {
  paid: 'Cobradas', pending: 'Pendientes', partial: 'Parciales',
  overdue: 'Vencidas', cancelled: 'Canceladas', all: 'Todas',
};

const COLUMN_LABELS = {
  porcentajeAdministracion: '%',
  valorCuota: 'Valor cuota',
  importeAdministracion: 'Importe admin.',
  gananciaAgente: 'Ganancia agente',
  gananciaOficina: 'Ganancia oficina',
  liquidacionPropietario: 'Liquidación prop.',
};

const DEFAULT_FILTERS = {
  status: 'paid',
};

const ContractSummaryReport = () => {
  const dispatch = useDispatch();
  const { sellers } = useSelector((state) => state);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [reportData, setReportData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    dispatch(getAllSellers('active'));
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = await dispatch(fetchPaymentsExcel(filters));
        if (cancelled) return;
        setColumns(payload?.columns || []);
        setReportData(payload?.data || []);
        setCount(payload?.count ?? (payload?.data || []).length);
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.message || err.message);
        setColumns([]);
        setReportData([]);
        setCount(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    filters.sellerId,
    filters.propertyId,
    filters.officeId,
    filters.rentalId,
    filters.status,
    filters.currency,
    filters.dateFrom,
    filters.dateTo,
  ]);

  // Propiedades derivadas de los datos del reporte
  const propertiesFromReport = React.useMemo(() => {
    const map = new Map();
    reportData.forEach((row) => {
      const id = row.propertyId || row.idPropiedad;
      if (id && !map.has(id)) {
        map.set(id, {
          id,
          title: row.propiedad || row.property || row.titulo || `Propiedad #${id}`,
        });
      }
    });
    return Array.from(map.values());
  }, [reportData]);

  const formatValue = (key, value) => {
    if (value == null || value === '') return '-';
    const currencyKeys = [
      'valorCuota',
      'importeAdministracion',
      'gananciaAgente',
      'gananciaOficina',
      'liquidacionPropietario'
    ];
    if (currencyKeys.includes(key)) {
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        minimumFractionDigits: 0
      }).format(value || 0);
    }
    if (key === 'porcentajeAdministracion') {
      return `${value}%`;
    }
    if (key.toLowerCase().includes('fecha')) {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleDateString('es-AR');
    }
    return value;
  };

  const getColumnLabel = (column) => COLUMN_LABELS[column] || column;

  const visibleColumns = React.useMemo(() => columns.filter((column) => {
    if (column !== 'agente' && column !== 'oficina') return true;
    return reportData.some((row) => row[column] != null && row[column] !== '');
  }), [columns, reportData]);

  /* ====================== PDF ====================== */
  const buildFilterSummary = () => {
    const out = [];
    const toArr = (v) => Array.isArray(v) ? v : (typeof v === 'string' ? v.split(',') : [v]);

    if (filters.sellerId) {
      const sellerMap = new Map((sellers || []).map((s) => [String(s.id), s]));
      const labels = toArr(filters.sellerId).map((id) => {
        const s = sellerMap.get(String(id).trim());
        return s ? `${s.name || ''} ${s.last_name || ''}`.trim() : `#${id}`;
      }).filter(Boolean).join(', ');
      if (labels) out.push(`Agente: ${labels}`);
    }
    if (filters.propertyId) {
      out.push(`Propiedad: ${toArr(filters.propertyId).map((id) => `#${id}`).join(', ')}`);
    }
    if (filters.status) {
      out.push(`Estado: ${toArr(filters.status).map((v) => STATUS_LABELS[String(v).trim()] || v).join(', ')}`);
    }
    if (filters.currency) out.push(`Moneda: ${filters.currency}`);
    if (filters.dateFrom) out.push(`Desde: ${filters.dateFrom}`);
    if (filters.dateTo) out.push(`Hasta: ${filters.dateTo}`);
    return out.length ? out.join('  ·  ') : 'Sin filtros aplicados';
  };

  const handleDownloadPdf = () => {
    if (!visibleColumns.length || !reportData.length) return;
    setDownloadingPdf(true);
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const PAGE_W = doc.internal.pageSize.getWidth();
      const PAGE_H = doc.internal.pageSize.getHeight();
      const MARGIN = 32;
      const usableW = PAGE_W - MARGIN * 2;

      // ---- anchos de columnas (proporcionales al contenido) ----
      doc.setFontSize(8);
      const measured = visibleColumns.map((col) => {
        const headerW = doc.getTextWidth(String(getColumnLabel(col)));
        let maxW = 0;
        const sample = Math.min(reportData.length, 60);
        for (let i = 0; i < sample; i += 1) {
          const txt = formatValue(col, reportData[i][col]);
          const w = doc.getTextWidth(String(txt));
          if (w > maxW) maxW = w;
        }
        return Math.min(Math.max(headerW, maxW) + 12, 170);
      });
      let widths;
      const sumW = measured.reduce((a, b) => a + b, 0);
      if (sumW > usableW) {
        const scale = usableW / sumW;
        widths = measured.map((w) => Math.max(w * scale, 32));
      } else {
        const extra = (usableW - sumW) / measured.length;
        widths = measured.map((w) => w + extra);
      }
      const totalTableW = widths.reduce((a, b) => a + b, 0);

      const generatedAt = new Date().toLocaleString('es-AR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
      const summary = buildFilterSummary();

      const drawDocHeader = (compact = false) => {
        let y = MARGIN;
        if (!compact) {
          doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(31, 41, 55);
          doc.text('Reporte de Cuotas', MARGIN, y); y += 18;

          doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(107, 114, 128);
          doc.text('Detalle de cuotas con cálculo de administración.', MARGIN, y); y += 14;

          doc.setFontSize(9); doc.setTextColor(75, 85, 99);
          doc.text(`Generado: ${generatedAt}    ·    Registros: ${reportData.length}`, MARGIN, y); y += 14;

          doc.setFont('helvetica', 'italic'); doc.setFontSize(9); doc.setTextColor(55, 65, 81);
          const lines = doc.splitTextToSize(`Filtros — ${summary}`, usableW);
          lines.forEach((l) => { doc.text(l, MARGIN, y); y += 11; });
          y += 4;
          doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.5);
          doc.line(MARGIN, y, PAGE_W - MARGIN, y);
          y += 10;
        } else {
          doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(31, 41, 55);
          doc.text('Reporte de Cuotas (continuación)', MARGIN, y); y += 16;
        }
        return y;
      };

      const drawTableHeader = (yStart) => {
        const rowH = 22;
        doc.setFillColor(243, 244, 246);
        doc.rect(MARGIN, yStart, totalTableW, rowH, 'F');
        doc.setDrawColor(209, 213, 219); doc.setLineWidth(0.5);
        doc.line(MARGIN, yStart + rowH, MARGIN + totalTableW, yStart + rowH);
        doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(31, 41, 55);

        let cx = MARGIN;
        visibleColumns.forEach((col, i) => {
          const label = String(getColumnLabel(col));
          const trimmed = doc.splitTextToSize(label, widths[i] - 6)[0] || label;
          doc.text(trimmed, cx + 4, yStart + 14);
          cx += widths[i];
        });
        return yStart + rowH;
      };

      let y = drawDocHeader(false);
      y = drawTableHeader(y);

      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(31, 41, 55);
      const minLineH = 10;
      const padding = 4;
      const bottomLimit = PAGE_H - MARGIN - 18;

      reportData.forEach((row, idx) => {
        const cells = visibleColumns.map((col, i) => {
          const raw = formatValue(col, row[col]);
          const lines = doc.splitTextToSize(String(raw), widths[i] - padding * 2);
          return lines;
        });
        const linesMax = cells.reduce((m, ls) => Math.max(m, ls.length), 1);
        const rowH = Math.max(18, linesMax * minLineH + 6);

        if (y + rowH > bottomLimit) {
          doc.addPage();
          y = drawDocHeader(true);
          y = drawTableHeader(y);
          doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(31, 41, 55);
        }

        if (idx % 2 === 1) {
          doc.setFillColor(249, 250, 251);
          doc.rect(MARGIN, y, totalTableW, rowH, 'F');
        }

        let cx = MARGIN;
        cells.forEach((lines, i) => {
          lines.forEach((line, li) => {
            doc.text(String(line), cx + padding, y + 12 + li * minLineH);
          });
          cx += widths[i];
        });

        doc.setDrawColor(243, 244, 246); doc.setLineWidth(0.3);
        doc.line(MARGIN, y + rowH, MARGIN + totalTableW, y + rowH);
        y += rowH;
      });

      // Footer con paginación en todas las páginas
      const totalPages = doc.getNumberOfPages();
      for (let p = 1; p <= totalPages; p += 1) {
        doc.setPage(p);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(156, 163, 175);
        doc.text('By Raíces — Reporte de Cuotas', MARGIN, PAGE_H - 16);
        doc.text(`Página ${p} de ${totalPages}`, PAGE_W - MARGIN, PAGE_H - 16, { align: 'right' });
      }

      const today = new Date().toISOString().slice(0, 10);
      doc.save(`reporte-cuotas-${today}.pdf`);
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className={style.container}>
      <div className={style.header}>
        <h1 className={style.title}>
          <FaChartLine className={style.titleIcon} />
          Reporte de Cuotas
        </h1>
        <p className={style.subtitle}>
          Detalle completo de todas las cuotas con cálculo de administración.
        </p>
      </div>

      <RentalFilters
        value={filters}
        onChange={setFilters}
        onClear={() => setFilters(DEFAULT_FILTERS)}
        statusOptions={PAYMENT_STATUS_OPTIONS}
        sellers={sellers || []}
        properties={propertiesFromReport}
      />

      {loading && <div className={style.loading}>Cargando reporte...</div>}
      {error && <div className={style.error}>Error: {error}</div>}

      {!loading && !error && reportData.length === 0 && (
        <div className={style.noData}>
          No hay datos disponibles.
        </div>
      )}

      {!loading && !error && reportData.length > 0 && (
        <div className={style.tableWrapper}>
          <div className={style.tableHeader}>
            <div>
              <FaFileExcel /> Total de registros: {count}
            </div>
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf || !visibleColumns.length}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: '#dc2626',
                color: '#fff',
                border: 'none',
                padding: '8px 14px',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: downloadingPdf ? 'wait' : 'pointer',
                opacity: downloadingPdf ? 0.7 : 1,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              <FaFilePdf />
              {downloadingPdf ? 'Generando PDF...' : 'Descargar PDF'}
            </button>
          </div>
          <table className={style.table}>
            <thead>
              <tr>
                {visibleColumns.map(column => (
                  <th key={column} title={column}>{getColumnLabel(column)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reportData.map((row, index) => (
                <tr key={row.paymentId || index}>
                  {visibleColumns.map(column => (
                    <td
                      key={`${column}-${index}`}
                      title={column}
                    >
                      {formatValue(column, row[column])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ContractSummaryReport;
